<template>
  <div class="ml-auto mr-auto min-w-[20rem] max-w-4xl">
    <form class="mb-4" @submit.prevent="() => doSearch()">
      <label
        for="domain-search"
        class="text-lm-contrast dark:text-dm-contrast sr-only mb-2 text-sm font-medium"
        >{{ $t("dac.search_cta") }}</label
      >

      <div
        class="bg-lm border-lm-contrast/10 dark:bg-dm-contrast/5 dark:border-dm-contrast/10 placeholder-lm-contrast/25 dark:placeholder-dm-contrast/25 group flex gap-1.5 rounded-lg border-2 p-3 focus-within:outline-none focus-within:ring focus-within:ring-opacity-25"
      >
        <div class="flex flex-grow-[99] basis-72 gap-x-4">
          <div
            class="pointer-events-none hidden shrink-0 items-center pl-2 sm:flex"
          >
            <MagnifyingGlassIcon
              class="text-auto-25 group-focus-within:text-primary h-7 w-7 transition"
            />
          </div>

          <input
            ref="dacInput"
            v-model="domain"
            id="domain-search"
            type="text"
            class="text-lm-contrast dark:text-dm-contrast placeholder:text-lm-contrast/25 dark:placeholder:text-dm-contrast/25 h-11 w-full border-none bg-transparent text-lg !ring-0"
            :placeholder="
              transferContext
                ? $t('dac.input_placeholder_transfer_context')
                : $t('dac.input_placeholder')
            "
          />

          <Transition
            enter-active-class="ease-out duration-100"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-out duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <template v-if="domain?.length">
              <button
                type="button"
                class="text-lm-contrast/25 dark:text-dm-contrast/25 hover:text-lm-contrast/50 dark:hover:text-dm-contrast/50 p-2 transition"
                tabindex="-1"
                @click="handleClearSearch"
              >
                <BackspaceIcon class="h-7 w-7" />
              </button>
            </template>
          </Transition>
        </div>

        <UpmButton
          type="submit"
          :is-processing="isProcessing"
          :disabled="!domain?.length"
          class="is-primary flex-grow justify-center border-none bg-gradient-to-tl !px-3 !py-2 text-lg lg:!px-5"
        >
          <MagnifyingGlassIcon class="h-6 w-6 sm:hidden" />
          <I18n path="dac.search_cta" class="hidden sm:block" />
        </UpmButton>
      </div>
    </form>

    <div class="relative">
      <template v-if="!resultsCount && !!skeletonRows">
        <UpmSkeletonWrapper :active="isProcessing">
          <ul
            role="list"
            class="dark:divide-dm-contrast/10 divide-lm-contrast/10 mt-6 divide-y"
          >
            <li
              v-for="(s, sI) in Array(skeletonRows)"
              :key="`skeleton-row-${sI}`"
              class="animate-pulse! flex flex-wrap items-center gap-x-4 px-4 py-6 pl-6 sm:flex-nowrap"
            >
              <UpmSkeleton class="hidden h-6 w-6 sm:block" />
              <div class="shrink grow space-y-2">
                <UpmSkeleton class="h-3 w-20" />
                <UpmSkeleton class="h-5 w-44" />
                <UpmSkeleton class="hidden h-3 w-36 sm:block" />
              </div>
              <div class="my-2 w-full sm:my-0 sm:w-auto">
                <UpmSkeleton class="h-5 w-14 shrink-0" />
              </div>
              <UpmSkeleton class="h-10 w-full shrink-0 sm:w-14 xl:w-32" />
            </li>
          </ul>
        </UpmSkeletonWrapper>

        <Transition
          enter-active-class="ease-out duration-100 transition"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-out duration-100 transition"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="!!domain && !resultsCount"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <UpmSpinner class="text-primary h-10 w-10" />
          </div>
        </Transition>
      </template>

      <Transition
        enter-active-class="ease-out duration-300 transition-all"
        enter-from-class="top-1/3 opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="ease-out duration-300 transition-all"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <OverlayCard v-if="hasError">
          <template #title>{{ $t("dac.error_msg") }}</template>
          <template #subtitle>{{ $t("dac.try_again_instruction") }}</template>
          <template #icon>
            <ExclamationTriangleIcon class="text-danger" />
          </template>
        </OverlayCard>

        <OverlayCard v-else-if="hasNoResults">
          <template #title>{{ $t("dac.no_results_msg", { domain }) }}</template>
          <template #subtitle>{{ $t("dac.try_again_instruction") }}</template>
        </OverlayCard>

        <OverlayCard v-else-if="!domain && !!skeletonRows">
          <template #title>{{
            $t("dac.it_starts_with_a_domain_msg")
          }}</template>
          <template #subtitle>{{ $t("dac.begin_your_search_msg") }}</template>
          <template #icon>
            <MagnifyingGlassCircleIcon />
          </template>
        </OverlayCard>
      </Transition>
    </div>

    <template v-if="resultsCount">
      <ul
        role="list"
        class="divide-lm-contrast/10 dark:divide-dm-contrast/10 mt-6 divide-y"
      >
        <li
          v-for="{
            billing_cycle_years,
            billing_summary,
            can_transfer,
            domain,
            is_available,
            is_numerical,
            order_url,
            percentage_saving,
            price_discounted_formatted,
            price_formatted,
            sld,
            tld,
          } in results"
          :key="domain"
          class="group items-center gap-x-4 gap-y-1 px-4 py-4 transition-colors sm:flex sm:flex-wrap sm:pl-6"
          :class="{
            'dark:hover:bg-dm-contrast/5 hover:bg-lm-contrast/2': is_available,
          }"
        >
          <div class="hidden sm:block">
            <CheckCircleIcon v-if="is_available" class="text-success h-7 w-7" />
            <ArrowsRightLeftIcon v-else class="text-auto-25 h-7 w-7" />
          </div>

          <div class="max-w-full grow-[99]">
            <!-- Status Label -->
            <I18n
              tag="p"
              class="text-tiny uppercase leading-4"
              :class="{
                'text-success': is_available,
                'text-auto-50': !is_available,
              }"
              :path="
                is_available ? 'dac.available_label' : 'dac.transfer_label'
              "
            />

            <div class="gap-x-8 overflow-hidden sm:flex sm:items-center">
              <!-- Domain Name (SLD + TLD) -->
              <p
                class="text-auto grow truncate text-xl sm:text-2xl"
                :class="{
                  'text-rtl': !is_numerical,
                  'transition-transform sm:group-hover:translate-x-2':
                    is_available,
                }"
              >
                <span class="font-light opacity-80">{{ sld }}</span>
                <strong
                  :class="{
                    'font-semibold': true,
                    'group-hover:text-success transition-colors': is_available,
                  }"
                  >{{ tld }}</strong
                >
              </p>

              <div
                class="flex shrink-0 flex-row-reverse items-center gap-x-3 sm:flex-row"
              >
                <!-- 'Save X%' tag -->
                <template v-if="is_available">
                  <SavingTag :key="domain" :percentage="percentage_saving" />
                </template>

                <!-- Price -->
                <template v-if="is_available">
                  <I18n
                    tag="p"
                    path="dac.price_by_n_years"
                    class="text-auto-50 mr-auto shrink-0 text-sm"
                    :count="billing_cycle_years"
                  >
                    <template #price>
                      <span
                        class="text-auto text-lg font-semibold sm:text-xl"
                        >{{
                          price_discounted_formatted || price_formatted
                        }}</span
                      >
                    </template>
                  </I18n>
                </template>
              </div>
            </div>

            <!-- Billing Summary -->
            <template v-if="is_available">
              <UpmMarkdown tag="p" class="text-auto-50 text-tiny">{{
                billing_summary
              }}</UpmMarkdown>
            </template>

            <template v-else-if="transferContext">
              <I18n
                tag="p"
                path="dac.transfer_renewal_price_summary"
                class="text-auto-50 text-tiny grow basis-72"
                :values="{ percentage: percentage_saving }"
              >
                <template #price>{{
                  $tc("dac.price_by_n_years", billing_cycle_years, {
                    price: price_discounted_formatted || price_formatted,
                  })
                }}</template>
              </I18n>
            </template>
          </div>

          <template v-if="is_available">
            <UpmButton
              as="anchor"
              :href="order_url"
              class="hover:!bg-success text-success border-success hover:text-success-contrast mt-2 w-full shrink-0 grow justify-center border-2 bg-transparent hover:bg-opacity-100 sm:mt-0 sm:w-auto"
            >
              <ShoppingCartIcon class="h-4 w-4 xl:hidden" />
              <I18n path="dac.add_to_basket_cta" class="sm:max-xl:hidden" />
            </UpmButton>
          </template>

          <template v-else-if="transferContext">
            <UpmButton
              as="anchor"
              :href="order_url"
              class="text-auto border-auto-25 hover:border-auto-75 mt-2 w-full shrink-0 grow justify-center border-2 bg-transparent sm:mt-0 sm:w-auto"
            >
              <ArrowsRightLeftIcon class="h-4 w-4 xl:hidden" />
              <I18n path="dac.transfer_cta" class="sm:max-xl:hidden" />
            </UpmButton>
          </template>

          <template v-else-if="can_transfer">
            <I18n
              tag="p"
              path="dac.transfer_instruction"
              class="text-auto-50 text-tiny grow basis-72"
              :values="{ percentage: percentage_saving }"
            >
              <template #action>
                <a :href="order_url" class="underline">{{
                  $t("dac.clicking_here_cta").toLocaleLowerCase()
                }}</a>
              </template>
              <template #tld>{{ tld.toUpperCase() }}</template>
              <template #price>{{
                $tc("dac.price_by_n_years", billing_cycle_years, {
                  price: price_discounted_formatted || price_formatted,
                })
              }}</template>
            </I18n>
          </template>

          <!-- Unavailable -->
          <template v-else>
            <UpmButton
              disabled
              class="text-auto border-auto-25 mt-2 w-full shrink-0 grow justify-center border-2 sm:mt-0 sm:w-auto"
            >
              <I18n path="dac.unavailable_label" />
            </UpmButton>
          </template>
        </li>
      </ul>

      <template v-if="hasMoreResults">
        <div class="mt-6 px-4">
          <UpmButton
            class="border-lm-contrast/10 text-auto bg-lm-contrast/2 dark:border-dm-contrast/20 d dark:bg-dm-contrast/5 hover:text-auto-75 w-full justify-center rounded-3xl text-lg"
            @click="loadMore"
            :is-processing="isProcessing"
            >{{ $t("dac.load_more_results_cta") }}</UpmButton
          >
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  ArrowsRightLeftIcon,
  ShoppingCartIcon,
  MagnifyingGlassIcon,
} from "@heroicons/vue/20/solid";
import {
  BackspaceIcon,
  CheckCircleIcon,
  MagnifyingGlassCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";
import UpmSpinner from "@src/components/UpmSpinner.vue";
import UpmSkeleton from "@src/components/UpmSkeleton.vue";
import UpmSkeletonWrapper from "@src/components/UpmSkeletonWrapper.vue";
import { useI18n } from "@src/composables/i18n";
import { useDac } from "./composables/dac";
import UpmButton from "@src/components/UpmButton.vue";
import UpmMarkdown from "@src/components/UpmMarkdown.vue";
import SavingTag from "./components/SavingTag.vue";
import OverlayCard from "./components/OverlayCard.vue";
import { useUiStore } from "@src/stores/UiStore";
import { storeToRefs } from "pinia";

const { $t, $tc, I18n } = useI18n({ packs: ["dac"] });
const { isSmPlus } = storeToRefs(useUiStore());

const props = withDefaults(
  defineProps<{
    coupons?: string[];
    currencyCode?: string;
    limit?: number;
    orderConfigUrl?: string;
    query?: string;
    skeletonCount?: number | null;
    tlds?: string[];
    transferContext?: boolean;
  }>(),
  {
    coupons: () => [],
    currencyCode: "",
    limit: 10,
    orderConfigUrl: "",
    query: "",
    skeletonCount: null,
    tlds: () => [],
    transferContext: false,
  }
);

const dacInput = ref<InstanceType<typeof HTMLInputElement>>();

function handleClearSearch() {
  clearSearch();
  dacInput?.value?.focus();
}

const {
  domain,
  hasError,
  hasMoreResults,
  hasNoResults,
  isProcessing,
  results,
  resultsCount,
  // Methods
  clearSearch,
  doSearch,
  loadMore,
} = useDac(props);

const skeletonRows = computed(() => {
  const responsiveCount = isSmPlus.value ? 3 : 2;
  if (props.skeletonCount === null) return responsiveCount;
  if (!props.skeletonCount && (hasNoResults.value || hasError.value))
    return responsiveCount;
  return props.skeletonCount;
});

// Watch for changes in the query prop to update the domain. This is useful in
// scenarios where one wants to manually relay a query to the component.

watch(
  () => props.query,
  () => {
    if (!props.query?.length) return;
    domain.value = props.query;
  },
  { immediate: true }
);
</script>
